import React from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  Checkbox,
  Divider,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Typography,
  MenuItem,
  ListItemText,
  FormControl,
  Box,
} from "@mui/material";
import ResetFilterButton from "components/molecules/ResetFilterButton";
import translations from "translations";
import { errorReportQueryFilterSelectorFamily, errorReportStatusAtom } from "../recoil";
import TitleWithFilterCounter from "pages/AccessPermissionManagement/CustomFilter/TitleWithFilterCounter";

interface ErrorReportFilterProps {
  filterType: "errorReportStatus";
  title: string;
}

const ErrorReportFilter: React.FC<ErrorReportFilterProps> = ({ filterType, title }) => {
  const queryOptions = useRecoilValue(errorReportStatusAtom);
  const [filters, setFilters] = useRecoilState(errorReportQueryFilterSelectorFamily(filterType));
  const resetFilters = () => setFilters([]);

  const options = (queryOptions ?? [])
    .map<{ key: string; value: string; label: string }>((entry) => {
      return {
        key: entry.key,
        value: entry.key === "Open" ? "New" : entry.key,
        label: entry.key === "Resolved" ? "" : entry.value,
      };
    })
    .filter((i) => !!i.label)
    .sort((a, b) => a.label.localeCompare(b.label));

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setFilters(typeof value === "string" ? value.split(",") : value);
  };

  return (
    <>
      <FormControl sx={{ m: 1 }} size="small" aria-label={filterType}>
        <Select
          labelId={filterType}
          id={filterType}
          multiple
          displayEmpty
          sx={{ borderRadius: "0px" }}
          value={filters}
          onChange={handleChange}
          input={<OutlinedInput />}
          renderValue={(selected) => {
            if (selected.length === 0 || selected === undefined) {
              return <Typography>{title}</Typography>;
            }
            return <TitleWithFilterCounter selectedLength={selected.length} title={title} />;
          }}
        >
          {options?.map(({ key, value, label }) => (
            <MenuItem key={key} value={value}>
              <Checkbox checked={filters.includes(value)} />
              <ListItemText primary={label} />
            </MenuItem>
          ))}
          <Divider />
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <ResetFilterButton title={translations.globals.button.reset} resetFilters={resetFilters} />
          </Box>
        </Select>
        {!!filters.length && (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <ResetFilterButton title={translations.globals.button.reset} resetFilters={resetFilters} />
          </Box>
        )}
      </FormControl>
    </>
  );
};

export default ErrorReportFilter;
